import React, { ComponentType, ReactElement, useEffect, useState } from "react"

import { useGlobalData } from "@/app/global_data"
import { withAppData } from "@/app/providers/app_data.with"
import { Spinner } from "@/components/layouts/spinner"

const REFRESH_MS = 1000

export function withGlobalData<P extends {}>(
  Component: ComponentType<P>
): React.FC<P> {
  return withAppData(function WithGlobalData(props): ReactElement {
    const globalData = useGlobalData()
    // to avoid hydration errors, dont render anything until the useEffect() triggers on the client
    const [isHydrated, setIsHydrated] = useState<boolean>(false)
    const [reloadToggle, setReloadToggle] = useState<boolean>(false)

    useEffect(() => {
      if (!isHydrated) setIsHydrated(true)
    }, [])

    useEffect(() => {
      if (globalData.isLoaded()) return

      if (globalData.getError()?.hasError)
        throw new Error(globalData.getError()?.message)

      // if still loading, then retry after 1 sec
      const timeout = setTimeout(
        () => setReloadToggle(!reloadToggle),
        REFRESH_MS
      )

      return () => clearTimeout(timeout)
    })

    if (!isHydrated) return <></>

    return (
      <>
        {globalData.isLoaded() ?
          <Component {...props} />
        : <Loading entity={globalData.getEntity()} />}
      </>
    )
  })
}

export function Loading({ entity }) {
  return (
    <Spinner hasTitle title={`Loading ${entity?.getName() ?? "data"}...`} />
  )
}
